import { useEffect, useMemo, useRef, useState } from 'react'
import { Combobox, Loader, ScrollArea, TextInput, useCombobox } from '@mantine/core'
import { IconSearch, IconX } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { useDebounce } from '@/hooks'
import { buildPatientLookupSearchQuery } from '@/utils/patientSearch'
import { logError } from '@/utils/errorUtils'
import { fmtTime } from './scheduleCalendar.tokens'
import {
  APPOINTMENT_SEARCH_LIMIT,
  buildAppointmentSearchQuery,
  sortByProximity,
  toAppointmentSearchResults,
  type AppointmentSearchResult,
} from './appointment-search'

const MIN_QUERY_LENGTH = 2

const formatResultDate = (iso: string): string => {
  const date = new Date(iso)
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

const formatResultTime = (result: AppointmentSearchResult): string =>
  result.end ? `${fmtTime(result.start)}–${fmtTime(result.end)}` : fmtTime(result.start)

export interface AppointmentSearchProps {
  practitionerIds: string[]
  doctorNameById?: Record<string, string>
  referenceMs?: number
  onPick: (result: AppointmentSearchResult) => void
  onClear: () => void
}

export const AppointmentSearch = ({
  practitionerIds,
  doctorNameById,
  referenceMs,
  onPick,
  onClear,
}: AppointmentSearchProps) => {
  const medplum = useMedplum()
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })

  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<AppointmentSearchResult[]>([])
  const debouncedQuery = useDebounce(query, 300)
  const pickedLabelRef = useRef<string | null>(null)
  const practitionerScope = practitionerIds.join(',')

  useEffect(() => {
    const trimmed = debouncedQuery.trim()
    if (pickedLabelRef.current !== null) {
      if (pickedLabelRef.current === trimmed) pickedLabelRef.current = null
      setLoading(false)
      return
    }
    if (trimmed.length < MIN_QUERY_LENGTH || !practitionerScope) {
      setResults([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    const referenceIso = new Date(referenceMs ?? Date.now()).toISOString()
    medplum
      .searchResources('Patient', buildPatientLookupSearchQuery(trimmed, '_count=20'))
      .then(async (patients) => {
        const patientIds = patients
          .map((patient) => patient.id)
          .filter((id): id is string => Boolean(id))
        if (patientIds.length === 0) {
          return []
        }
        const practitionerIds = practitionerScope.split(',')
        const [upcoming, past] = await Promise.all(
          (['upcoming', 'past'] as const).map((direction) =>
            medplum.searchResources(
              'Encounter',
              buildAppointmentSearchQuery(patientIds, practitionerIds, direction, referenceIso),
            ),
          ),
        )
        return toAppointmentSearchResults([...upcoming, ...past])
      })
      .then((found) => {
        if (!cancelled) setResults(found)
      })
      .catch((error) => {
        if (cancelled) return
        logError('schedule', 'Appointment search failed', error)
        setResults([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, practitionerScope, medplum, referenceMs])

  const visibleResults = useMemo(
    () => sortByProximity(results, referenceMs ?? Date.now()).slice(0, APPOINTMENT_SEARCH_LIMIT),
    [results, referenceMs],
  )

  const options = useMemo(
    () =>
      visibleResults.map((result) => {
        const doctorName = result.practitionerId
          ? doctorNameById?.[result.practitionerId]
          : undefined
        return (
          <Combobox.Option value={result.encounterId} key={result.encounterId}>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-[#1F2733]">{result.patientName}</span>
              <span className="text-xs text-[#8A9099]">
                {formatResultDate(result.start)} · {formatResultTime(result)}
                {doctorName ? ` · ${doctorName}` : ''}
              </span>
            </div>
          </Combobox.Option>
        )
      }),
    [visibleResults, doctorNameById],
  )

  const clear = () => {
    pickedLabelRef.current = null
    setQuery('')
    setResults([])
    onClear()
  }

  const handleSubmit = (encounterId: string) => {
    const result = visibleResults.find((item) => item.encounterId === encounterId)
    if (!result) return
    pickedLabelRef.current = result.patientName
    setQuery(result.patientName)
    combobox.closeDropdown()
    onPick(result)
  }

  return (
    <Combobox store={combobox} withinPortal shadow="md" onOptionSubmit={handleSubmit}>
      <Combobox.Target>
        <TextInput
          size="xs"
          value={query}
          onChange={(event) => {
            pickedLabelRef.current = null
            setQuery(event.currentTarget.value)
            combobox.openDropdown()
            combobox.updateSelectedOptionIndex()
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          leftSection={<IconSearch size={14} className="opacity-50" />}
          rightSection={
            loading ? (
              <Loader size="xs" />
            ) : query ? (
              <IconX
                size={14}
                className="cursor-pointer opacity-60"
                aria-label="Clear search"
                onMouseDown={(event) => {
                  event.preventDefault()
                  clear()
                }}
              />
            ) : null
          }
          classNames={{
            input: 'rounded-md border border-gray-300 !text-sm',
            root: 'w-[300px]',
          }}
          placeholder="Find appointments by patient..."
          aria-label="Find appointments by patient"
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={query.trim().length < MIN_QUERY_LENGTH}>
        <Combobox.Options>
          <ScrollArea.Autosize mah={280} type="scroll">
            {loading && (
              <Combobox.Empty>
                <span className="text-xs text-[#8A9099]">Searching...</span>
              </Combobox.Empty>
            )}
            {!loading && options.length === 0 && (
              <Combobox.Empty>
                <span className="text-xs text-[#8A9099]">No appointments found</span>
              </Combobox.Empty>
            )}
            {!loading && options}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
